import './GeoResponse.scss';
import cn from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import * as Dfns from 'date-fns/fp';
import { MdSave } from 'react-icons/md';
import { DATE_FORMAT, DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { getStandardDeviation } from '@blisspointmedia/bpm-types/dist/brandHealthMetricsUtils';
import { AUDIO, STREAMING } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import * as S from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { DatePicker } from '@Components/DatePicker';
import { Size } from '@Components/designConstants';
import { DmaMap, DMACode, GeoData } from '@Components/DmaMap/DmaMap';
import { Dropdown } from '@Components/Dropdown';
import { EmptyState } from '@Components/EmptyState';
import { KpiPicker, useKpiPickerData } from '@Components/KpiPicker';
import { Page } from '@Components/Page';
import { FullPageSpinner } from '@Components/Spinner';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { CellRenderer } from '@Components/StickyTable/StickyTable';
import { useSetError } from '@src/redux/modals';
import { DivergentColorMap20, makeSteppedHeatMap } from '@src/utils/colors';
import { convertSVGStringToPNGURI, download } from '@src/utils/download-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import {
  COLOR_RANGE_SENSITIVITY,
  DEFAULT_LAG,
  DISABLED_COLOR,
  DOWNLOAD_HEIGHT,
  DOWNLOAD_WIDTH,
  IMPRESSIONS_CUTOFF_PERCENTAGE,
  Lag,
  LAG_NAMES,
  TABLE_HEADERS
} from './geo-response-constants';
import { useGeoLags, useGeoResponseData } from './use-geo-response-data';

interface GeoResponseProps {
  channel?: S.Prefix;
}

export const GeoResponse = ({ channel = STREAMING }: GeoResponseProps) => {
  const setError = useSetError();
  const { cid: company } = useCompanyInfo();

  const [selectedKpi, setKpi] = useState<string>();
  const { kpiMap, defaultKpi } = useKpiPickerData({ useCrossChannelKpis: true });
  // Falls back to the default whenever the selection isn't in the current company's KPI map (e.g.
  // before the user picks one, or after switching companies).
  const kpi = selectedKpi && kpiMap[selectedKpi] ? selectedKpi : defaultKpi || undefined;

  const [lag, setLag] = useState<Lag>();

  const [selection, setSelection] = useState<string>();
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    start: Dfns.format(DATE_FORMAT, Dfns.subMonths(1, new Date())),
    end: Dfns.format(DATE_FORMAT, new Date())
  }));

  const mapSvgRef = useRef<SVGSVGElement | null>(null);

  const { data: validLags, error: lagsError } = useGeoLags(kpi, company, channel);
  // Hold the geo fetch until the current lag is confirmed valid for this KPI -- otherwise a KPI
  // switch fires /geo with the previous KPI's lag before /geo_lags resolves, and if the new KPI
  // lacks that lag the lambda errors on the missing S3 file.
  const validLag = lag && validLags?.includes(lag) ? lag : undefined;
  const { data: geoData, error: geoDataError } = useGeoResponseData(
    kpi,
    company,
    validLag,
    channel,
    dateRange
  );

  useEffect(() => {
    if (lagsError) {
      setError({ message: `Failed to get lag data: ${lagsError.message}`, reportError: lagsError });
    }
  }, [lagsError, setError]);

  useEffect(() => {
    if (geoDataError) {
      setError({
        message: `Failed to get geo data: ${geoDataError.message}`,
        reportError: geoDataError
      });
    }
  }, [geoDataError, setError]);

  useEffect(() => {
    if (validLags) {
      setLag(currentLag => {
        if (currentLag === undefined) {
          return validLags.includes(DEFAULT_LAG) ? DEFAULT_LAG : validLags[0];
        }
        if (!validLags.includes(currentLag)) {
          return validLags[0];
        }
        return currentLag;
      });
    }
  }, [validLags]);

  const renderColor = useMemo(() => {
    if (!geoData) {
      return undefined;
    }
    const dmas = Object.values(geoData);
    const totalImpressions = dmas.reduce((sum, dmaData) => sum + dmaData.impressions, 0);
    const impressionsCutoff = totalImpressions * IMPRESSIONS_CUTOFF_PERCENTAGE;

    const geoIndices = dmas
      .filter(dmaData => dmaData.impressions > impressionsCutoff)
      .map(dmaData => dmaData.index);
    const mean = geoIndices.reduce((sum, geoIndex) => sum + geoIndex, 0) / geoIndices.length;
    const std = getStandardDeviation(geoIndices);

    const heatMap = makeSteppedHeatMap({
      min: mean - COLOR_RANGE_SENSITIVITY * std,
      max: mean + COLOR_RANGE_SENSITIVITY * std,
      midpoint: mean,
      stops: DivergentColorMap20
    });

    return (dma: DMACode) => {
      const dmaData = geoData[dma];
      if (!dmaData) {
        return 'transparent';
      }
      return dmaData.impressions < impressionsCutoff ? DISABLED_COLOR : heatMap(dmaData.index);
    };
  }, [geoData]);

  const tableData = useMemo(
    () => Object.values(geoData ?? {}).sort((a, b) => b.index - a.index),
    [geoData]
  );

  const downloadPNG = useCallback(() => {
    if (!mapSvgRef.current) {
      return;
    }
    const uri = convertSVGStringToPNGURI(
      mapSvgRef.current.outerHTML,
      DOWNLOAD_WIDTH,
      DOWNLOAD_HEIGHT
    );
    download(uri, `${kpi}_geo_heatmap`);
  }, [kpi]);

  const handleMouseEnter = useCallback((dma: string) => () => setSelection(dma), []);
  const handleMouseLeave = useCallback(() => setSelection(undefined), []);

  // BPMTable types `rowData` as `object` and `data` as the row type; at runtime they're the GeoData
  // row and the cell's value.
  const cellRenderer: CellRenderer<GeoData> = useCallback(
    ({ data, rowData, style, classes }) => {
      const { dma } = rowData as GeoData;
      const value: unknown = data;
      return (
        <span
          style={style}
          className={cn(classes)}
          onMouseEnter={handleMouseEnter(dma)}
          onMouseLeave={handleMouseLeave}
        >
          {typeof value === 'number' ? value.toLocaleString('en-us') : String(value)}
        </span>
      );
    },
    [handleMouseLeave, handleMouseEnter]
  );

  const isDateOutsideRange = useCallback(
    (date: string) => Dfns.format(DATE_FORMAT, new Date()) < date,
    []
  );

  const renderTooltip = useCallback(
    (dma: DMACode) => {
      if (!geoData?.[dma]) {
        return null;
      }
      return <GeoResponseTooltip dmaData={geoData[dma]} />;
    },
    [geoData]
  );

  const lagOptions = useMemo(
    () => (validLags ?? []).map(lagKey => ({ label: LAG_NAMES[lagKey], value: lagKey })),
    [validLags]
  );

  const pageTitle = `${channel === AUDIO ? 'Audio' : 'Streaming'} Geo Response`;

  // Company info is loaded once `company` is set, so an empty default means the company has no
  // cross-channel KPIs at all -- not that they're still loading.
  const hasNoKpis = Boolean(company) && !defaultKpi;
  const hasError = Boolean(lagsError || geoDataError);
  const hasNoData =
    validLags?.length === 0 ||
    (geoData !== undefined && Object.values(geoData).every(dmaData => !dmaData.impressions));
  const isLoading = !hasNoKpis && !hasError && !hasNoData && (!validLags || !geoData);

  return (
    <Page
      title={pageTitle}
      pageType={pageTitle}
      isLoading={isLoading}
      actions={
        <div className='geo-response-actions'>
          {kpi && (
            <KpiPicker kpi={kpi} onChange={setKpi} kpiMap={kpiMap} disableTypeSelector noBreak />
          )}
          {validLags && lag && (
            <Dropdown
              size={Size.LG}
              label='Lag'
              value={lag}
              options={lagOptions}
              onChange={setLag}
            />
          )}
          <DatePicker
            size={Size.LG}
            range={dateRange}
            onChange={setDateRange}
            isOutsideRange={isDateOutsideRange}
            datePresets
          />
          <Button
            size={Size.LG}
            leadingElement={<MdSave />}
            disabled={!geoData || hasNoData}
            onClick={downloadPNG}
          >
            PNG
          </Button>
        </div>
      }
    >
      <div className='geo-response' data-testid='geo-response'>
        <GeoResponseBody
          hasNoKpis={hasNoKpis}
          hasError={hasError}
          hasNoData={hasNoData}
          isLoading={isLoading}
        >
          <ChartContainer className='geo-map-container' title='Response Index by DMA'>
            <div className='geo-map' data-testid='geo-response-map'>
              <AutoSizer
                renderProp={({ width, height }) => (
                  <DmaMap
                    width={width}
                    height={height}
                    selection={selection}
                    renderColor={renderColor}
                    renderTooltip={renderTooltip}
                    ref={mapSvgRef}
                  />
                )}
              />
            </div>
          </ChartContainer>
          <ChartContainer className='geo-table'>
            <BPMTable
              csvDownload={`${kpi}_geo_response_index`}
              data={tableData}
              headers={TABLE_HEADERS}
              cellRenderer={cellRenderer}
              cellClassName='geo-entry'
            />
          </ChartContainer>
        </GeoResponseBody>
      </div>
    </Page>
  );
};

interface GeoResponseBodyProps {
  hasNoKpis: boolean;
  hasError: boolean;
  hasNoData: boolean;
  isLoading: boolean;
  children: ReactNode;
}

const GeoResponseBody = ({
  hasNoKpis,
  hasError,
  hasNoData,
  isLoading,
  children
}: GeoResponseBodyProps) => {
  if (hasNoKpis) {
    return (
      <EmptyState
        title='No cross-channel KPIs'
        subtitle='Set up cross-channel KPIs in KPI Mapping to see geo response.'
      />
    );
  }
  if (hasError) {
    return (
      <EmptyState
        title="Couldn't load geo response data"
        subtitle='Try refreshing the page or selecting a different KPI.'
      />
    );
  }
  if (hasNoData) {
    return (
      <EmptyState title='No geo response data' subtitle='Try a different KPI or date range.' />
    );
  }
  if (isLoading) {
    return <FullPageSpinner />;
  }
  return children;
};

interface GeoResponseTooltipProps {
  dmaData: GeoData;
}

const GeoResponseTooltip = ({
  dmaData: { impressions, responses, households, index, name }
}: GeoResponseTooltipProps) => (
  <div className='geo-response-tooltip'>
    <div className='tooltip-title'>{name}</div>
    <div className='tooltip-rows'>
      <TooltipRow label='Response Index' value={index} />
      <TooltipRow label='Impressions' value={impressions} />
      <TooltipRow label='Responses' value={responses} />
      <TooltipRow label='Households' value={households} />
    </div>
  </div>
);

interface TooltipRowProps {
  label: string;
  value: number;
}

const TooltipRow = ({ label, value }: TooltipRowProps) => (
  <div className='tooltip-row'>
    <span className='tooltip-label'>{label}</span>
    <span className='tooltip-value'>{value.toLocaleString('en-us')}</span>
  </div>
);
